import * as React from 'react';
import * as FeatureFlags from '../../Service/FeatureFlags';
import TreeImageUpload from './TreeImageUpload';
import BigNumberInput from './BigNumberInput';
import GeolocationData from './GeolocationData';
import DebugData from './DebugData';
import Joi from 'joi';
import message from 'antd/es/message';
import DBService, { TABLE_TREES } from '../../Service/DBService';
import UAParser from "ua-parser-js";
import { FormInstance, FormProps } from 'antd/lib/form';
import { Contract, TreeData, User } from 'tree-wallet-client';
import { Alert, Col, Form, Row, Typography } from 'antd';
import {
  TWPositionData,
  watchHistory, calculateGpsAccuracyState
} from '../../Service/GeolocationService';
import { ConnectionState, GlobalContext } from '../../Contexts/GlobalContext';
import { entriesToFormData } from '../../Helper/DataHelper';
import SyncQueue from "./SyncQueue/SyncQueue";
import { v4 as uuidv4 } from 'uuid';
import TreeTypeSelect from "./TreeTypeSelect";
import { EventedArrayEvent } from '../../Dependency/EventedArray';

const { Text } = Typography;

const layout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 8 }
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 16 }
  },
};

interface TreeFormProps extends FormProps {
  user: User;
  contract: Contract;
  treeData?: TreeData[];
  isPlanterValidator?: boolean;
}

interface TreeFormState {
  lat?: number;
  lng?: number;
  speed?: number;
  accuracy?: number;
  user: User;
  displayCoordinates: any;
  coordinates?: TWPositionData;
  number: number;
  hasImage: boolean;
  keepBestPosition: boolean;
  hasEnoughAccuracy: boolean;
  /** Set while a photo is compressed and queued: the queue button shows a spinner for the count. */
  savingToQueue: boolean;
  showGPSCoordinates: boolean;
  displayConfirmation: boolean;
  onlineStatus: ConnectionState;
  simulatedAccuracy?: number;
  unansweredFields: string[];
  validationAttempted: boolean;
  previewImage?: string;
  bannerMessage?: string;
  bannerType?: 'success' | 'info' | 'warning' | 'error';
}

export const treeValidationSchema = Joi.object({
  contractId: Joi.number().required(),
  userId: Joi.number().required(),
  typeId: Joi.number().required(),
  lng: Joi.number().required(),
  lat: Joi.number().required(),
  speed: Joi.number(),
  number: Joi.number().required(),
  accuracy: Joi.number().required(),
  altitude: Joi.number().allow(null),
  altitude_accuracy: Joi.number(),
  photo: Joi.any().required(),
  gpsHistory: Joi.string(),
  userAgent: Joi.string(),
  uuid: Joi.string()
});

export default class TreeForm extends React.Component<TreeFormProps, TreeFormState> {
  declare context: React.ContextType<typeof GlobalContext>;
  static contextType = GlobalContext;
  private formRef: React.RefObject<FormInstance>;
  private syncQueueRef: React.RefObject<SyncQueue>;
  // On iOS, scrolling the option list of a Select can be chained to the page.
  // Remember where the current touch started so that such a scroll does not
  // close the dropdown (see handleScroll).
  private touchStartedInSelectDropdown: boolean;
  // Values of the form as they were when the photo was shot (see the `onProcessingChange` handler of the
  // photo field): the payload is saved at this moment, next the tree species list is reset to default state
  private valuesAtShot: any = null;

  constructor(props: TreeFormProps) {
    super(props);
    this.onFinish = this.onFinish.bind(this);
    this.onFinishFailed = this.onFinishFailed.bind(this);
    this.formRef = React.createRef();
    this.syncQueueRef = React.createRef();
    this.touchStartedInSelectDropdown = false;
    this.handleTreeProcessed = this.handleTreeProcessed.bind(this);
    this.state = {
      displayCoordinates: undefined,
      coordinates: undefined,
      lat: undefined,
      lng: undefined,
      user: props.user,
      number: 1,
      showGPSCoordinates: false,
      hasImage: false,
      // Set when "Click to upload" opens the camera: from then on only a better
      // position replaces the stored one, which is the position saved in the queue
      // payload.
      keepBestPosition: false,
      hasEnoughAccuracy: false,
      savingToQueue: false,
      displayConfirmation: false,
      onlineStatus: ConnectionState.online,
      simulatedAccuracy: undefined,
      unansweredFields: [],
      validationAttempted: false,
    }
    this.validateQuestionsBeforePhoto = this.validateQuestionsBeforePhoto.bind(this);
  }

  resetForm() {
    this.setState({
      coordinates: undefined,
      showGPSCoordinates: false,
      savingToQueue: false,
      hasImage: false,
      keepBestPosition: false,
      unansweredFields: [],
      validationAttempted: false,
      previewImage: undefined,
    },
      () => {
        // `typeId` is not reset here but as soon as the photo is shot (see the `onProcessingChange` handler
        // of the photo field): a reset remounts the field's child, which closed the species list when the
        // tree landed in the queue while the next one was already being prepared.
        this.formRef.current?.resetFields(['photo', 'number']);
      }
    );
  }

  async componentDidMount() {
    const { onlineStatus } = this.context;
    await DBService.openDb();
    this.calculateAccuracy(onlineStatus, watchHistory.values[0]);
    watchHistory.on(EventedArrayEvent.add, async (newCoords: TWPositionData) => {
      this.calculateAccuracy(onlineStatus, newCoords);
    });

    // Add scroll listener to close dropdowns on scroll (Mobile requirement)
    window.addEventListener('scroll', this.handleScroll, true);
    window.addEventListener('touchstart', this.handleTouchStart, { passive: true });
    window.addEventListener('touchend', this.handleTouchEnd, { passive: true });
    window.addEventListener('touchcancel', this.handleTouchEnd, { passive: true });
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.handleScroll, true);
    window.removeEventListener('touchstart', this.handleTouchStart);
    window.removeEventListener('touchend', this.handleTouchEnd);
    window.removeEventListener('touchcancel', this.handleTouchEnd);
  }

  handleTouchStart = (event: TouchEvent) => {
    const target = event.target as HTMLElement | null;
    this.touchStartedInSelectDropdown = !!(target && target.closest && target.closest('.ant-select-dropdown'));
  };

  handleTouchEnd = () => {
    this.touchStartedInSelectDropdown = false;
  };

  handleScroll = (event: any) => {
    if (event.target && event.target.closest && event.target.closest('.ant-select-dropdown')) {
      return;
    }

    // A drag that started on the option list belongs to the dropdown, even when
    // iOS scrolls the page along with it: keep the dropdown open in that case.
    if (this.touchStartedInSelectDropdown) {
      return;
    }

    if (document.activeElement instanceof HTMLInputElement && document.activeElement.closest('.ant-select')) {
      return;
    }

    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    const openSelects = document.querySelectorAll('.ant-select-open');
    openSelects.forEach((el) => {
      if (el instanceof HTMLElement) el.blur();
    });
  };

  // Centre of the "show queue" item, which the camera popup collapses towards when the photo is
  // taken. Looked up in the DOM because the queue - and its button - live in SyncQueue, and all
  // that is needed here is a screen position.
  getQueueScreenPoint = () => {
    const queueItem = document.querySelector('.btn-open-offline-queue');
    if (!queueItem) {
      return null;
    }
    const rect = queueItem.getBoundingClientRect();
    return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
  };

  calculateAccuracy(onlineStatus: ConnectionState | undefined, newCoords: TWPositionData) {
    const { coordinates: currentCoords, hasImage, keepBestPosition, simulatedAccuracy } = this.state;

    const newState = calculateGpsAccuracyState(currentCoords, newCoords, keepBestPosition || hasImage, simulatedAccuracy);
    this.setState({ ...newState, onlineStatus });
  }

  validateQuestionsBeforePhoto() {
    const { formRef } = this;
    const { contract } = this.props;
    const unanswered: string[] = [];
    // A new photo starts: what was read at the previous shot is stale by now (the queue clears it too,
    // this covers the attempts that never made it that far).
    this.valuesAtShot = null;

    if (formRef.current) {
      const values = formRef.current.getFieldsValue();
      if (values.typeId === undefined || values.typeId === null) unanswered.push('typeId');
      if (contract.preferences?.advanced && (values.number === undefined || values.number === null)) unanswered.push('number');

      if (unanswered.length > 0) {
        // Trigger antd validation to show red borders
        formRef.current.validateFields(unanswered).catch(() => { });
        return false;
      }
    }
    this.setState({ unansweredFields: [], validationAttempted: false, bannerMessage: undefined });

    // The checks passed, so the press of "Click to upload" opens the camera: from
    // then on only a better position replaces the stored one, which is the position
    // saved in the queue payload.
    this.setState({ keepBestPosition: true });

    return true;
  }

  async handleTreeProcessed(formValues: any, photoFile: File) {
    const { coordinates } = this.state;
    const { user, treeData, contract } = this.props;
    // The species field is emptied when the photo is shot (see the `onProcessingChange` handler of the
    // photo field): the values the payload needs are the ones read at that moment.
    const values = this.valuesAtShot || formValues;
    this.valuesAtShot = null;

    // Check if we have GPS coordinates
    if (!coordinates || !coordinates.lng || !coordinates.lat) {
      console.error('GPS coordinates not available');
      message.error('GPS coordinates are required. Please wait for GPS to be ready.');
      return;
    }

    const data = {
      ...values,
      photo: photoFile,
      number: contract.preferences?.advanced ? values.number : 1,
      lng: coordinates && coordinates.lng,
      lat: coordinates && coordinates.lat,
      speed: coordinates && coordinates.speed,
      accuracy: coordinates && coordinates.accuracy,
      altitude: (coordinates && coordinates.altitude !== undefined && coordinates.altitude !== null) ? coordinates.altitude : 0,
      altitude_accuracy: coordinates && coordinates.altitudeAccuracy,
      userId: user.id,
      contractId: contract.id,
      typeId: treeData && treeData[values['typeId']].id,
      // The API still reads gpsHistory and passes it straight to the repository:
      // it has to keep receiving an array, not nothing.
      gpsHistory: JSON.stringify(watchHistory.values),
      userAgent: JSON.stringify(new UAParser().getResult()),
      uuid: uuidv4()
    };

    try {
      await treeValidationSchema.validateAsync(data).catch(err => console.error(err, data));
      await this.queueItem(entriesToFormData(Object.entries(data)));
    } catch (err) {
      console.error('Error processing tree:', err);
      message.error('Failed to process tree');
    }
  }

  async onFinish(values: any) {
    const { coordinates } = this.state;
    const { user, treeData, contract } = this.props;

    const data = {
      ...values,
      number: contract.preferences?.advanced ? values.number : 1,
      lng: coordinates && coordinates.lng,
      lat: coordinates && coordinates.lat,
      speed: coordinates && coordinates.speed,
      accuracy: coordinates && coordinates.accuracy,
      altitude: (coordinates && coordinates.altitude !== undefined && coordinates.altitude !== null) ? coordinates.altitude : 0,
      altitude_accuracy: coordinates && coordinates.altitudeAccuracy,
      userId: user.id,
      contractId: contract.id,
      typeId: treeData && treeData[values['typeId']].id,
      // see handleTreeProcessed: the API still expects gpsHistory
      gpsHistory: JSON.stringify(watchHistory.values),
      userAgent: JSON.stringify(new UAParser().getResult()),
      uuid: uuidv4()
    };

    try {
      await treeValidationSchema.validateAsync(data).catch(err => console.error(err, data));
      await this.queueItem(entriesToFormData(Object.entries(data)));
    } catch (err) {
      console.error(err, data);
    }
  }

  async queueItem(formData: FormData) {
    // Convert FormData to object for IndexedDB storage
    const treeData: any = {};
    for (const [key, value] of (formData as any).entries()) {
      treeData[key] = value;
    }

    await DBService.putValue(TABLE_TREES, treeData);

    // Ensure queue count & queue content is updated with new tree hole
    await this.syncQueueRef.current?.fetchQueueData();
    // Refresh free storage value
    this.syncQueueRef.current?.refreshStorageEstimate();

    // Reported once the tree hole really is inside the queue: the caller waits for this method before
    // closing the loading indicator that covers the compression and tree hole saving
    message.info('Tree was added to queue');

    this.resetForm();
  }

  onFinishFailed(errorInfo: any) {
    console.error('Failed:', errorInfo);
  }

  render() {
    const { treeData, contract } = this.props;
    const {
      lat,
      lng,
      user,
      number,
      coordinates,
      // hasImage,
      displayCoordinates,
      showGPSCoordinates,
      hasEnoughAccuracy,
      savingToQueue,
      unansweredFields,
      bannerMessage,
      bannerType,
    } = this.state;

    return (
      <div style={{ paddingTop: '20px' }}>
        {bannerMessage && (
          <Alert
            message={bannerMessage}
            type={bannerType}
            showIcon
            style={{ marginBottom: '16px' }}
          />
        )}
        <h2 style={{ textAlign: 'center' }}>New Tree ({contract.description})</h2>
        <div style={{ textAlign: 'center' }}>
          <SyncQueue treetypesList={treeData} ref={this.syncQueueRef} saving={savingToQueue} />
        </div>
        <GeolocationData coordinates={displayCoordinates} />
        <Form
          {...layout}
          ref={this.formRef}
          name="basic"
          initialValues={{ remember: true, lat, lng, user, number }}
          onFinish={this.onFinish}
          onFinishFailed={this.onFinishFailed}
          onValuesChange={() => {
            const { unansweredFields, validationAttempted } = this.state;
            if (validationAttempted) {
              const allValues = this.formRef.current?.getFieldsValue();
              const newUnanswered = unansweredFields.filter((f: string) => allValues[f] === undefined || allValues[f] === null);
              if (newUnanswered.length !== unansweredFields.length) {
                this.setState({ unansweredFields: newUnanswered });
              }
            }
          }}
          className={'treeform'}
        >
          <Form.Item
            label={<span style={{ color: unansweredFields.includes('typeId') ? '#ff4d4f' : 'inherit' }}>Tree Type</span>}
            name="typeId"
            rules={[{ required: true, message: 'Please set a description !' }]}
            validateStatus={unansweredFields.includes('typeId') ? 'error' : undefined}
          >
            <TreeTypeSelect
              treeData={treeData}
              contract={contract}
              formRef={this.formRef}
              name='typeId'
              formSetState={(data: any, callback: any) => { this.setState(data, callback) }}
            />
          </Form.Item>
          {contract.preferences?.advanced &&
            <Form.Item
              label={<span style={{ color: unansweredFields.includes('number') ? '#ff4d4f' : 'inherit' }}>Number</span>}
              name="number"
              rules={[{ required: true, message: 'Please set the number of tree planted !' }]}
              validateStatus={unansweredFields.includes('number') ? 'error' : undefined}
            >
              <BigNumberInput formRef={this.formRef} />
            </Form.Item>
          }
          <Form.Item
            label="Photo"
            name="photo"
            rules={[{ required: true, message: 'Please select a photo of the tree !' }]}
          >
            <TreeImageUpload
              enabled={hasEnoughAccuracy}
              name='photo'
              // The kept position, not the live one: it is the best accuracy since the camera was
              // opened and the one this photo will be queued with (see calculateGpsAccuracyState).
              gpsAccuracy={coordinates?.accuracy ?? null}
              getCollapsePoint={this.getQueueScreenPoint}
              formRef={this.formRef}
              formSetState={(data: any, callback: any) => {
                this.setState(data, callback);
                if (data.hasImage) {
                  this.setState({ unansweredFields: [] });
                }
              }}
              onClick={this.validateQuestionsBeforePhoto}
              isPlanterValidator={this.props.isPlanterValidator}
              onTreeProcessed={this.handleTreeProcessed}
              onProcessingChange={(processing: boolean) => {
                // Reset the tree species list when a photo has just been shot
                if (processing && !this.valuesAtShot) {
                  // The photo has just been shot: the species field restarts ready to select next new tree
                  this.valuesAtShot = this.formRef.current?.getFieldsValue();
                  this.formRef.current?.resetFields(['typeId']);
                }
                this.setState({ savingToQueue: processing });
              }}
            />
          </Form.Item>
          {showGPSCoordinates && coordinates &&
            <>
              <Row>
                <Col span={6}><Text strong>Latitude</Text></Col>
                <Col span={12} offset={2}>{coordinates.lat}</Col>
              </Row>
              <Row>
                <Col span={6}><Text strong>Longitude</Text></Col>
                <Col span={12} offset={2}>{coordinates.lng}</Col>
              </Row>
              <Row>
                <Col span={6}><Text strong>Altitude</Text></Col>
                <Col span={12} offset={2}>{coordinates.altitude}</Col>
              </Row>
              <Row>
                <Col span={6}><Text strong>Altitude Acc</Text></Col>
                <Col span={12} offset={2}>{coordinates.altitudeAccuracy}</Col>
              </Row>
              {/* <Row>
                <Col span={6}><Text strong>Orientation</Text></Col>
                <Col span={12} offset={2}>{coordinates.orientation ? `${Math.round(coordinates.orientation)}°` : '-'}</Col>
              </Row> */}
            </>
          }
        </Form>
        {FeatureFlags.getFeatureFlag('debug') &&
          <DebugData
            coordinates={coordinates}
            onAccuracyUpdate={(simulatedAccuracy: number) => {
              this.setState({ simulatedAccuracy });
              const coordinates = this.state.coordinates;
              if (coordinates) {
                const { onlineStatus } = this.context;
                this.calculateAccuracy(onlineStatus, coordinates);
              }
            }}
          />
        }
      </div>
    );
  }
}
